import React, { useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

/**
 * Props for SQLGenerationSection component
 */
interface SQLGenerationSectionProps {
  /** The complete SQL code to display */
  sqlCode: string;
  /** Optional custom title for the section */
  title?: string;
  /** Optional custom description */
  description?: string;
  /** Maximum height for preview before scrolling */
  maxPreviewHeight?: string;
}

/**
 * SQLGenerationSection Component
 *
 * Displays a unified SQL code block with copy functionality. Shows a partially
 * collapsed preview by default with scrollable overflow.
 *
 * @component
 * @example
 * ```tsx
 * <SQLGenerationSection
 *   sqlCode={generatedSQL}
 *   title="Setup SQL Commands"
 *   description="Run these commands in Snowflake to complete setup"
 * />
 * ```
 */
export const SQLGenerationSection: React.FC<SQLGenerationSectionProps> = ({
  sqlCode,
  title = 'Setup SQL',
  description = 'Copy and run these commands in your Snowflake account',
  maxPreviewHeight = '240px',
}) => {
  const [copied, setCopied] = useState<boolean>(false);

  /**
   * Copies SQL to clipboard
   */
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(sqlCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy SQL:', err);
    }
  };

  return (
    <motion.div
      className="bg-dark-card-background border border-gray-200 rounded-lg p-4 sm:p-5"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="text-sm text-dataops-dark">{title}</h4>
          <p className="mt-1 text-xs text-secondary-text">{description}</p>
        </div>
        <motion.button
          type="button"
          onClick={() => {
            void handleCopy();
          }}
          className="ml-3 inline-flex items-center justify-center px-4 py-3 border border-dataops-primary-blue shadow-sm text-xs rounded-md text-primary-text bg-white hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dataops-primary-blue transition-all duration-150"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          aria-label={copied ? 'SQL copied to clipboard' : 'Copy SQL to clipboard'}
        >
          {copied ? (
            <motion.span
              className="flex items-center gap-1.5"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500 }}
            >
              <CheckIcon className="h-4 w-4 text-green-highlight" aria-hidden="true" />
              Copied!
            </motion.span>
          ) : (
            <>
              <ClipboardDocumentIcon className="h-4 w-4 mr-1.5" aria-hidden="true" />
              Copy SQL
            </>
          )}
        </motion.button>
      </div>

      <div
        className="bg-white border border-gray-300 rounded-md p-3 overflow-auto"
        style={{ maxHeight: maxPreviewHeight }}
      >
        <pre className="font-mono text-xs text-primary-text whitespace-pre-wrap break-words">{sqlCode}</pre>
      </div>

      <p className="mt-3 text-xs text-secondary-text">
        Scroll to view all commands. Click &ldquo;Copy SQL&rdquo; to copy everything at once.
      </p>
    </motion.div>
  );
};
